import {
  Column,
  openDialog,
  Table,
  TableRow,
  TableRowAction,
  TableRowActionsData,
} from 'platform/components';
import {Align, Text} from 'platform/foundation';

import {isNonEmptyArray} from 'ramda-adjunct';

import {useGetCustomTenantCatalogueUsageQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {usePermissions, useVehicleCatalogue} from '@dms/shared';

import {buildArray, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useRemapProgress} from '../hooks/useRemapProgress';
import {
  CustomCatalogueMakeGroup,
  getCustomModels,
  getMakeGroupKey,
  groupUsageByMake,
} from '../utils/groupUsageByMake';
import {CatalogueMakeLabel} from './CatalogueMakeLabel';
import {ModelFamiliesCell} from './ModelFamiliesCell';
import {RemapMakeDialog} from './RemapMakeDialog';
import {RemapModelsDialog} from './RemapModelsDialog';

const columns: Column[] = [
  {width: '20%', element: <Align left>{i18n.t('entity.vehicle.labels.vehicleType')}</Align>},
  {width: '20%', element: <Align left>{i18n.t('entity.vehicle.labels.make')}</Align>},
  {width: '20%', element: <Align left>{i18n.t('entity.vehicle.labels.model')}</Align>},
  {
    width: '15%',
    element: <Align left>{i18n.t('entity.customCatalogue.labels.vehicleCount')}</Align>,
  },
  {width: '25%', element: <Align left>{i18n.t('entity.customCatalogue.labels.itemType')}</Align>},
];

export function CustomCatalogueTable(props: RequiredTestIdProps) {
  const {data: usage} = useGetCustomTenantCatalogueUsageQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [vehicleCatalogue] = useVehicleCatalogue(undefined);
  const [canRemap] = usePermissions({permissionKeys: ['remapCustomCatalogueEntry']});
  const {keysInProgress, start} = useRemapProgress();

  const groups = groupUsageByMake(usage ?? []);

  const noPermissionDescription = canRemap
    ? undefined
    : i18n.t('general.notifications.noPermission');

  const getVehicleTypeLabel = (vehicleType: string) =>
    vehicleCatalogue?.vehicleTypeOptions?.find((option) => option.value === vehicleType)?.label ??
    vehicleType;

  const openRemapMakeDialog = (group: CustomCatalogueMakeGroup) =>
    openDialog(
      <RemapMakeDialog
        group={group}
        onSubmitted={start}
        data-testid={suffixTestId('remapMake', props)}
      />,
      {title: i18n.t('entity.customCatalogue.replaceMakeDialog.title', {make: group.make})}
    );

  const openRemapModelsDialog = (group: CustomCatalogueMakeGroup) =>
    openDialog(
      <RemapModelsDialog
        group={group}
        onSubmitted={start}
        data-testid={suffixTestId('remapModels', props)}
      />,
      {title: i18n.t('entity.customCatalogue.actions.replaceModels')}
    );

  // A model under a custom make cannot be replaced on its own - its target would have to belong to
  // a make that does not exist - so a custom make is replaced as a whole, models included.
  const getRowActions = (group: CustomCatalogueMakeGroup): TableRowActionsData => {
    const isInProgress = keysInProgress.includes(getMakeGroupKey(group));
    const commonAction = {
      icon: 'image/edit' as const,
      isDisabled: !canRemap || isInProgress,
      description: isInProgress
        ? i18n.t('entity.customCatalogue.notifications.replacementInProgress')
        : noPermissionDescription,
    };

    return {
      primary: group.isCustomMake
        ? buildArray<TableRowAction>().add({
            ...commonAction,
            title: i18n.t('entity.customCatalogue.actions.replaceMake'),
            onClick: () => openRemapMakeDialog(group),
          })
        : buildArray<TableRowAction>().when(isNonEmptyArray(getCustomModels(group)), {
            ...commonAction,
            title: i18n.t('entity.customCatalogue.actions.replaceModels'),
            onClick: () => openRemapModelsDialog(group),
          }),
    };
  };

  return (
    <Table columns={columns} data-testid={suffixTestId('table', props)}>
      {groups.map((group, index) => (
        <TableRow
          key={getMakeGroupKey(group)}
          actions={getRowActions(group)}
          data-testid={suffixTestId(`row-[${index}]`, props)}
        >
          <Text size="small">{getVehicleTypeLabel(group.vehicleType)}</Text>
          <CatalogueMakeLabel vehicleType={group.vehicleType} make={group.make} />
          <ModelFamiliesCell models={group.models} />
          <Text size="small">{group.totalVehicleCount.toString()}</Text>
          <Text size="small">
            {i18n.t(
              group.isCustomMake
                ? 'entity.customCatalogue.itemType.customMake'
                : 'entity.customCatalogue.itemType.customModel'
            )}
          </Text>
        </TableRow>
      ))}
    </Table>
  );
}
